import { MenuItem, Tooltip } from "@components";
import {
  ChartMixedRegular,
  ClockLight,
  CoinsLight,
  CreditCardLight,
  DiagramProjectRegular,
  DotRegular,
  FileInvoiceLight,
  FileLinesLight,
  MegaphoneRegular,
  UserLight,
} from "@icons";
import RequirePermission from "@src/modules/member/components/require-permission";
import { PERMISSIONS } from "@src/modules/member/constants/permissions";
import { useMemberPath } from "@src/modules/member/hooks/use-member-path";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import {
  CLIENT_LIST_PATH,
  DASHBOARD_PATH,
  FORMS_LIST_PATH,
  INVOICE_LIST_PATH,
  PROJECT_LIST_PATH,
  RECURRING_INVOICE_LIST_PATH,
  SURCHARGE_LIST_PATH,
  TAXES_LIST_PATH,
  TIME_ENTRIES_LIST_PATH,
  TRANSACTIONS_LIST_PATH,
} from "../paths";
import Organization from "./organization";

interface MenuItemComponentPropTypes {
  minimized: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

const Divider = () => (
  <div className="flex w-full my-1 h-0 border-b border-gray-200" />
);

export const memberMainMenuList = [
  {
    key: "organization-menu-item",
    component: (props: MenuItemComponentPropTypes) => {
      return <Organization minimized={props.minimized} />;
    },
  },
  {
    key: "dashboard",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { path } = useMemberPath();

      return (
        <MenuItem
          to={path(DASHBOARD_PATH())}
          icon={<ChartMixedRegular width={16} />}
          label={t("member.main.dashboard.dashboard")}
          minimized={props.minimized}
          setMobileMenuOpen={props.setMobileMenuOpen}
        />
      );
    },
  },
  {
    key: "dashboard-divider",
    component: () => (
      <RequirePermission permission={PERMISSIONS.CLIENTS_VIEW_LIST}>
        <Divider />
      </RequirePermission>
    ),
  },
  {
    key: "client",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { path } = useMemberPath();

      return (
        <RequirePermission permission={PERMISSIONS.CLIENTS_VIEW_LIST}>
          <MenuItem
            to={path(CLIENT_LIST_PATH())}
            icon={<UserLight width={16} />}
            label={t("clients")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "after-client-divider",
    component: () => (
      <RequirePermission permission={PERMISSIONS.PROJECTS_VIEW_LIST}>
        <Divider />
      </RequirePermission>
    ),
  },
  {
    key: "projects",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { path } = useMemberPath();

      return (
        <RequirePermission permission={PERMISSIONS.PROJECTS_VIEW_LIST}>
          <MenuItem
            to={path(PROJECT_LIST_PATH())}
            icon={<DiagramProjectRegular width={16} />}
            label={t("member.main.projects.projectList")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "time-entries",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { path } = useMemberPath();

      return (
        <RequirePermission permission={PERMISSIONS.TIME_ENTRIES_VIEW_LIST}>
          <MenuItem
            to={path(TIME_ENTRIES_LIST_PATH())}
            icon={<ClockLight width={16} />}
            label={t("member.main.timeEntries.timeEntries")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "after-time-entries-divider",
    component: () => (
      <RequirePermission permission={PERMISSIONS.INVOICES_VIEW_LIST}>
        <Divider />
      </RequirePermission>
    ),
  },
  {
    key: "invoices-menu",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { path } = useMemberPath();

      return (
        <RequirePermission
          any={[
            PERMISSIONS.INVOICES_VIEW_LIST,
            PERMISSIONS.RECURRING_INVOICES_VIEW_LIST,
          ]}
        >
          <MenuItem
            submenu={[
              {
                key: "invoices",
                component: () => (
                  <RequirePermission
                    permission={PERMISSIONS.INVOICES_VIEW_LIST}
                  >
                    <MenuItem
                      to={path(INVOICE_LIST_PATH())}
                      icon={<DotRegular width={13} />}
                      label={t("member.main.invoices.invoices")}
                      minimized={props.minimized}
                      setMobileMenuOpen={props.setMobileMenuOpen}
                    />
                  </RequirePermission>
                ),
              },
              {
                key: "recurring-invoices",
                component: () => (
                  <RequirePermission
                    permission={PERMISSIONS.RECURRING_INVOICES_VIEW_LIST}
                  >
                    <MenuItem
                      to={path(RECURRING_INVOICE_LIST_PATH())}
                      icon={<DotRegular width={13} />}
                      label={t("member.main.invoices.recurring")}
                      minimized={props.minimized}
                      setMobileMenuOpen={props.setMobileMenuOpen}
                    />
                  </RequirePermission>
                ),
              },
            ]}
            icon={<FileInvoiceLight width={13} />}
            label={t("invoices")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "transactions",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { path } = useMemberPath();

      return (
        <RequirePermission permission={PERMISSIONS.TRANSACTIONS_VIEW_LIST}>
          <MenuItem
            to={path(TRANSACTIONS_LIST_PATH())}
            icon={<CreditCardLight width={16} />}
            label={t("transactions")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "fees",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { path } = useMemberPath();

      return (
        <RequirePermission
          any={[PERMISSIONS.TAXES_VIEW_LIST, PERMISSIONS.SURCHARGES_VIEW_LIST]}
        >
          <MenuItem
            submenu={[
              {
                key: "taxes",
                component: () => (
                  <RequirePermission permission={PERMISSIONS.TAXES_VIEW_LIST}>
                    <MenuItem
                      to={path(TAXES_LIST_PATH())}
                      icon={<DotRegular width={13} />}
                      label={t("taxes")}
                      minimized={props.minimized}
                      setMobileMenuOpen={props.setMobileMenuOpen}
                    />
                  </RequirePermission>
                ),
              },
              {
                key: "surcharges",
                component: () => (
                  <RequirePermission
                    permission={PERMISSIONS.SURCHARGES_VIEW_LIST}
                  >
                    <MenuItem
                      to={path(SURCHARGE_LIST_PATH())}
                      icon={<DotRegular width={13} />}
                      label={t("surcharges")}
                      minimized={props.minimized}
                      setMobileMenuOpen={props.setMobileMenuOpen}
                    />
                  </RequirePermission>
                ),
              },
            ]}
            icon={<CoinsLight />}
            label={t("feesAndTaxes")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
  {
    key: "after-fees-divider",
    component: () => (
      <RequirePermission permission={PERMISSIONS.FORMS_VIEW_LIST}>
        <Divider />
      </RequirePermission>
    ),
  },
  {
    key: "forms",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();
      const { path } = useMemberPath();

      return (
        <RequirePermission permission={PERMISSIONS.FORMS_VIEW_LIST}>
          <MenuItem
            to={path(FORMS_LIST_PATH())}
            icon={<FileLinesLight width={16} />}
            label={t("member.main.forms.forms")}
            minimized={props.minimized}
            setMobileMenuOpen={props.setMobileMenuOpen}
          />
        </RequirePermission>
      );
    },
  },
];

export const memberExtraMenuList = [
  {
    key: "change-logs",
    component: (props: MenuItemComponentPropTypes) => {
      const { t } = useTranslation();

      return (
        <button
          data-canny-changelog
          className={classNames(
            "flex rounded items-center justify-center hover:bg-white hover:text-secondary-700",
            {
              "w-11 h-11 mx-auto": props.minimized,
              "w-full h-8": !props.minimized,
            },
          )}
          aria-label={t("member.main.viewChangeLogs")}
        >
          <Tooltip label={t("member.main.viewChangeLogs")}>
            {props.minimized && <MegaphoneRegular width={20} />}
          </Tooltip>
          {!props.minimized && (
            <span className="ml-2 text-sm">
              {t("member.main.viewChangeLogs")}
            </span>
          )}
        </button>
      );
    },
  },
];
